import React, { useContext, useEffect, useMemo, useState } from 'react';
import { resourceCache } from 'use-async-resource';
import { NewRequestContext } from '../../context/NewRequestContext';
import { Client } from '../../models/table-models';
import {
    checkClientWarnings,
    useGetClients,
    useGetInvoices,
    useGetNewRequestBranchesByClient,
    UseNewRequestAddBranch,
} from '../../utils/clients-hooks';
import Select from 'react-select';
import { Alert, Button, Form, Table as BootstrapTable } from 'react-bootstrap';

import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { newRequestContainerSelectStyles } from '../../styles/selectStyles';
import { ClientSubForm } from './ClientSubForm';
import { useGetClientPrefix } from '../../utils/custom-hooks';
import { NewRequestTableButtons } from './NewRequestTableButtons';
import { AuthContext } from '../../context/AuthContext';
import { Role } from '../../utils/Enums/Roles';
import { NewRequestTableRow } from './NewRequestTableRow';
import { fetchBranchImage } from '../../utils/dashboard-hooks';
import { NameComparison, NewRequestBranch } from '../../models/dashboard-models';
import { BranchesContext } from '../../context/BranchesContext';
import { saveClient } from '../../utils/clients-hooks';
import CustomWarningModal from "../CustomWarningModal";
import { ClientRegistryCheck } from './ClientRegistryCheck';

export const NewRequestClientsTable = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { t: registryT } = useTranslation('translation', { keyPrefix: 'registryCheck' });
    const { hasPermission } = useContext(AuthContext);
    const isDistributor = !hasPermission([Role.COORDINATOR]) && (hasPermission([Role.DISTRIBUTOR]) || hasPermission([Role.INSTALLER]));

    const {
        selectedClient,
        updateClientAfterSave,
        clients,
        onClientSelect,
        getClients,
        selectedBranch,
        updateBranchAfterSave,
        getBranches,
    } = useContext(NewRequestContext);

    const [input, setInput] = useState('');
    const [editable, setEditable] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [pendingFormValue, setPendingFormValue] = useState<Client | null>(null);
    const [warningKeys, setWarningKeys] = useState<string[]>([]);
    const [nameComparison, setNameComparison] = useState<NameComparison | null>(null);
    const [ownerError, setOwnerError] = useState('');

    const { handleSubmit, reset, control, watch, formState, setValue } = useForm<Client>({
        defaultValues: useMemo(() => {
            return selectedClient;
        }, [selectedClient]),
        mode: 'all',
    });

    const { setLastSelectedBranch, updateBranchLocal } = useContext(BranchesContext);

    const clientVatPrefix = useGetClientPrefix(selectedClient.vatPrefix);
    useEffect(() => {
        setEditable(false);
        setInput('');
        setOwnerError('');
        reset(selectedClient);
    }, [selectedClient]);

    useEffect(() => {
        getClients({});
    }, []);

    const updateSelectedBranchHandler = async (selectedBranch: NewRequestBranch) => {
        try {
            if (selectedBranch.id) {
                const response = await UseNewRequestAddBranch(selectedBranch, selectedClient.id);
                if (response === undefined) return;

                // OPTIMISTIC UPDATE: Convert and update local state
                const mapBranch = {
                    id: selectedBranch.id || 0,
                    name: selectedBranch.name,
                    state: selectedBranch.state || 'GREEN',
                    phone: selectedBranch.phone || '',
                    latitude: selectedBranch.location.latitude,
                    longitude: selectedBranch.location.longitude,
                    city: selectedBranch.location.city,
                    assignedDistributor: (typeof selectedBranch.assignedDistributor === 'object'
                        ? selectedBranch.assignedDistributor.id
                        : selectedBranch.assignedDistributor) || 0,
                    branchAddress: selectedBranch.location.address,
                    companyName: selectedClient.companyName,
                    assignedTask: false,
                    testPeriod: false,
                    batteryPercent: 0,
                    maintenanceDays: 0,
                    nextMaintenanceDate: selectedBranch.nextMaintenanceDate || '',
                    oilPercent: 0,
                    batteryState: 'GREEN',
                    oilState: 'GREEN',
                    maintenanceState: 'GREEN',
                    groupName: selectedBranch.groupName,
                    price: 0,
                    paymentDate: '',
                };

                updateBranchLocal(mapBranch);

                updateBranchAfterSave(selectedBranch);
                setLastSelectedBranch(Number(selectedBranch.id));
                if (selectedClient.id) getBranches(selectedClient.id);
            }
        } catch (error) {
            console.log('Error');
        }

    };

    const actuallySubmit = async (formValue: Client, registryUpdate = false) => {
        // При нов клиент: подаденото име (напр. официалното от регистъра) има предимство пред полето за търсене.
        const params = editable || registryUpdate
            ? { companyName: formValue.companyName, id: selectedClient.id }
            : { companyName: formValue.companyName || input, id: -1 };

        const dataToPass = { ...formValue, vatPrefix: registryUpdate ? formValue.vatPrefix : clientVatPrefix, ...params };

        const response = await saveClient(dataToPass);
        if (registryUpdate && response === undefined) throw new Error('Client update failed');
        if (response === undefined) return;

        if (response !== undefined) {
            const { id } = response;
            const warns = (response as { warnings?: string[] }).warnings ?? [];
            setEditable(false);

            const newClient = { ...dataToPass, id };
            if (selectedClient.id) updateClientAfterSave(newClient);
            else onClientSelect(newClient);

            if (!selectedClient.id)
                toast.success(<CustomToastContent content={[t('clientSuccess')]} />, toastOptionsTopRight);
            else
                toast.success(<CustomToastContent content={[t('clientUpdate')]} />, toastOptionsTopRight);

            if (warns.length > 0) {
                setWarningKeys(warns);
                setShowWarning(true);
            }
        }

        if (selectedBranch.id && selectedClient.company === false) {
            const updateSelectedBranch = {
                ...selectedBranch,
                name: selectedClient.company ? selectedBranch.name : formValue.companyName,
            };
            await updateSelectedBranchHandler(updateSelectedBranch);
        }

        resourceCache(useGetClients).clear();
        getClients({});
        resourceCache(useGetInvoices).clear();
        resourceCache(useGetNewRequestBranchesByClient).clear();
        // REMOVED: resourceCache(fetchMapBranches).clear();
        resourceCache(fetchBranchImage).clear();
    };

    const selectClientHandler = (option: any) => {
        onClientSelect(option);
    };

    const onSubmitCheck = async (formValue: Client) => {
        setOwnerError('');
        if (editable) {
            const fields: [keyof Client, string][] = [
                ['companyAddress', 'companyAddress'], ['phone', 'phone'], ['contact', 'contact'], ['city', 'city'],
            ];
            if (formValue.company) {
                fields.push(['owner', 'owner']);
                if (formValue.ddc) fields.push(['vat', 'vat']);
            }
            const cleared = fields.filter(([key]) => typeof formValue[key] === 'string'
                && !String(formValue[key]).trim() && String(selectedClient[key] ?? '').trim()).map(([, label]) => label);
            if (cleared.length) {
                setOwnerError(registryT('cannotClearFields', {
                    fields: cleared.map(key => registryT(`fields.${key}`)).join(', '),
                    vatHint: cleared.includes('vat') ? registryT('clearVatHint') : '',
                }));
                return;
            }
        }
        const params = editable
            ? { companyName: formValue.companyName, id: selectedClient.id }
            : { companyName: input, id: -1 };

        const dataToPass: Client = { ...formValue, vatPrefix: clientVatPrefix, ...params };

        const res = await checkClientWarnings(dataToPass);
        const warns = res?.warnings ?? [];

        if (warns.length > 0) {
            setWarningKeys(warns);
            setNameComparison(res?.nameComparison ?? null);
            setPendingFormValue(dataToPass);
            setShowWarning(true);
            return; // ← do NOT save yet
        }

        await actuallySubmit(dataToPass);
    };

    return (
        <>
        {ownerError && <Alert variant='danger'>{ownerError}</Alert>}
        <Form
            id='newRequestClient'
            className='w-100 p-0 mt-2 mb-1'
            noValidate
            onSubmit={handleSubmit(onSubmitCheck)}
        >
            <BootstrapTable className='w-100 position-relative'>
                <thead>
                    <tr className='position-relative'>
                        <th colSpan={2} className='text-center border-top fw-bold'>
                            {watch('company') ? t('client') : t('individual')}
                        </th>
                    </tr>
                    <tr>
                        <td colSpan={2}>
                            {
                                <Controller
                                    name='company'
                                    {...{ control }}
                                    render={({ field }) => (
                                        <div className='d-flex w-100 justify-content-center gap-4'>
                                            <Form.Check
                                                id='company-radio-company'
                                                className='p-0 d-flex align-items-center gap-2 mb-0'
                                            >
                                                <Form.Check.Input
                                                    id='company-radio-company'
                                                    type='radio'
                                                    name='company'
                                                    className='m-0'
                                                    checked={!!field.value}
                                                    onChange={() => field.onChange(true)}
                                                    disabled={
                                                        !(!selectedBranch.id && !selectedClient.id) &&
                                                        selectedClient.id !== undefined &&
                                                        !editable
                                                    }
                                                />
                                                <Form.Check.Label
                                                    htmlFor='company-radio-company'
                                                    className='mb-0'
                                                    style={{
                                                        cursor:
                                                            !(!selectedBranch.id && !selectedClient.id) &&
                                                            selectedClient.id !== undefined &&
                                                            !editable
                                                                ? 'default'
                                                                : 'pointer',
                                                    }}
                                                >
                                                    {t('company')}
                                                </Form.Check.Label>
                                            </Form.Check>
                                            <Form.Check
                                                id='company-radio-individual'
                                                className='p-0 d-flex align-items-center gap-2 mb-0'
                                            >
                                                <Form.Check.Input
                                                    id='company-radio-individual'
                                                    type='radio'
                                                    name='company'
                                                    className='m-0'
                                                    checked={!field.value}
                                                    onChange={() => field.onChange(false)}
                                                    disabled={
                                                        !(!selectedBranch.id && !selectedClient.id) &&
                                                        selectedClient.id !== undefined &&
                                                        !editable
                                                    }
                                                />
                                                <Form.Check.Label
                                                    htmlFor='company-radio-individual'
                                                    className='mb-0'
                                                    style={{
                                                        cursor:
                                                            !(!selectedBranch.id && !selectedClient.id) &&
                                                            selectedClient.id !== undefined &&
                                                            !editable
                                                                ? 'default'
                                                                : 'pointer',
                                                    }}
                                                >
                                                    {t('individual')}
                                                </Form.Check.Label>
                                            </Form.Check>
                                        </div>
                                    )}
                                ></Controller>
                            }
                        </td>
                    </tr>
                </thead>
                <tbody className='d-flex flex-column'>
                    <NewRequestTableRow label={t('name')} id='client'>
                        {!selectedBranch.id ? (
                            watch('company') ? (
                                <Select
                                    className='w-100 px-0 me-1'
                                    isClearable
                                    isSearchable
                                    noOptionsMessage={(e) => {
                                        return <h6>{t('noClientsFound')}</h6>;
                                    }}
                                    onChange={(option) => selectClientHandler(option)}
                                    styles={{
                                        container: newRequestContainerSelectStyles,
                                    }}
                                    maxMenuHeight={150}
                                    value={selectedClient ?? undefined}
                                    getOptionValue={(option) => `${option.id}`}
                                    options={clients?.items}
                                    getOptionLabel={(option) => option.companyName}
                                    placeholder={input || t('selectClient')}
                                    inputValue={input ?? ''}
                                    onInputChange={setInput}
                                    onMenuClose={() => {
                                        setInput(input);
                                    }}
                                    blurInputOnSelect
                                    isDisabled={!!selectedClient.id}
                                />
                            ) : (
                                <Form.Control
                                    type='text'
                                    value={selectedClient.id !== undefined ? selectedClient.companyName : input}
                                    onChange={(e) => setInput(e.target.value)}
                                    required
                                    disabled={
                                        (!editable && selectedClient.id !== undefined) ||
                                        (editable && selectedClient.id !== undefined)
                                    }
                                />
                            )
                        ) : (
                            <Form.Group
                                controlId={`companyName`}
                                className={`w-100 me-1 d-flex align-items-center`}
                                style={{ padding: '4px 0px' }}
                            >
                                {/* <h1>{`Tukaa`}</h1> */}
                                <Controller
                                    name='companyName'
                                    {...{ control }}
                                    render={({ field }) => (
                                        <>
                                            <Form.Control
                                                type='text'
                                                disabled={selectedClient.id ? !editable : false}
                                                value={field.value ?? ''}
                                                ref={field.ref}
                                                name={field.name}
                                                onChange={field.onChange}
                                                onBlur={field.onBlur}
                                            />
                                        </>
                                    )}
                                ></Controller>
                            </Form.Group>
                        )}
                    </NewRequestTableRow>
                    <ClientSubForm
                        control={control}
                        watch={watch}
                        disabled={selectedClient.id ? !editable : false}
                        clientVatPrefix={clientVatPrefix}
                        formState={formState}
                        setValue={setValue}
                    />

                    {selectedClient.id && (
                        <tr className='w-100 d-flex'>
                            {!isDistributor && (
                                <NewRequestTableButtons
                                    leadingAction={!editable && selectedClient.company ? (
                                        <ClientRegistryCheck key={selectedClient.id} client={selectedClient}
                                            onConfirm={(client) => actuallySubmit(client, true)} />
                                    ) : undefined}
                                    formToSubmitId='newRequestClient'
                                    toggleEdit={() => {
                                        if (editable) reset();
                                        setEditable((prev) => !prev);
                                    }}
                                    isEdit={editable}
                                />
                            )}
                        </tr>
                    )}
                </tbody>
            </BootstrapTable>
            {!selectedClient.id && (
                <Button className='w-100' type='submit' form='newRequestClient' disabled={input.length < 1}>
                    {t('createClient')}
                </Button>
            )}
            <CustomWarningModal
                show={showWarning}
                warnings={warningKeys}
                onCancel={() => {
                    setShowWarning(false);
                    setPendingFormValue(null); // do nothing
                }}
                onConfirm={async () => {
                    setShowWarning(false);
                    if (pendingFormValue) {
                        await actuallySubmit(pendingFormValue); // real save happens only here
                        setPendingFormValue(null);
                    }
                }}
                nameComparison={nameComparison}
                onUseOfficialName={async () => {
                    setShowWarning(false);
                    if (pendingFormValue && nameComparison) {
                        setValue('companyName', nameComparison.official);
                        setInput(nameComparison.official);
                        await actuallySubmit({ ...pendingFormValue, companyName: nameComparison.official });
                        setPendingFormValue(null);
                    }
                }}
            />

        </Form>
        </>
    );
};
